import { useState, useRef, useEffect, useMemo, useCallback } from "react";


export function parseCascadeTags(raw) {
  if (!raw) return [];

  let parsed = raw;
  if (typeof raw === "string") {
    try { parsed = JSON.parse(raw); } catch { return []; }
  }

  if (!Array.isArray(parsed) || parsed.length === 0) return [];

  // Already array-of-combos (each element is itself an array)
  if (Array.isArray(parsed[0])) return parsed;

  // Single flat combo — wrap it
  return [parsed];
}

/* ─────────────────────────────────────────────────────────────
   getNextEligibleFilters
   ─────────────────────────────────────────────────────────────
   Given current active filterSteps, returns the keys + values
   that are NEXT in the combo chain (strict prefix match).

   e.g. after selecting scope=scope1, only the keys that
   directly follow scope in matching combos are offered.
───────────────────────────────────────────────────────────── */
function getNextEligibleFilters(kpiItems, filterSteps) {
  const active = filterSteps.filter((s) => s.values?.size);
  const kvm = new Map();

  for (const item of kpiItems) {
    for (const combo of parseCascadeTags(item.rawTags)) {
      if (!combo?.length) continue;

      if (!active.length) {
        // Root level — expose first key of every combo
        const t = combo[0];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
        continue;
      }

      // Strict prefix match through the active steps
      let pos = 0;
      let matched = true;
      for (const step of active) {
        if (
          pos >= combo.length ||
          combo[pos].key !== step.key ||
          !step.values.has(combo[pos].value)
        ) {
          matched = false;
          break;
        }
        pos++;
      }

      // Next key is immediately after the matched prefix
      if (matched && pos < combo.length) {
        const t = combo[pos];
        if (t?.key && t?.value) {
          if (!kvm.has(t.key)) kvm.set(t.key, new Set());
          kvm.get(t.key).add(t.value);
        }
      }
    }
  }

  return Array.from(kvm.entries()).map(([key, values]) => ({
    key,
    values: Array.from(values).sort(),
  }));
}

/* ─────────────────────────────────────────────────────────────
   kpiMatchesCascadeSteps  (exported — used by QuantativeList)
   ─────────────────────────────────────────────────────────────
   Returns true if the item matches ALL active filter steps
   (using subsequence/prefix matching within each combo).
───────────────────────────────────────────────────────────── */
export function kpiMatchesCascadeSteps(item, steps) {
  const active = steps.filter((s) => s.values?.size > 0);
  if (!active.length) return true;

  const combos = parseCascadeTags(item.rawTags);

  return combos.some((combo) => {
    let pos = 0;
    for (const step of active) {
      let found = false;
      for (let i = pos; i < combo.length; i++) {
        if (combo[i]?.key === step.key && step.values.has(combo[i]?.value)) {
          pos = i + 1;
          found = true;
          break;
        }
      }
      if (!found) return false;
    }
    return true;
  });
}

/* ─────────────────────────────────────────────────────────────
   KpiCascadeFilter  (default export)
   ─────────────────────────────────────────────────────────────
   Props:
     kpiList           – full list of KPI items (must have .rawTags)
     filterSteps       – [{ key: string, values: Set<string> }]
     onFilterStepsChange – (newSteps) => void
───────────────────────────────────────────────────────────── */
const KpiCascadeFilter = ({ kpiList = [], filterSteps = [], onFilterStepsChange }) => {
  const [openKey, setOpenKey] = useState(null);
  const ref = useRef(null);

  /* Close dropdown on outside click */
  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpenKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const activeSteps = filterSteps.filter((s) => s.values?.size);

  /* Path-aware next eligible keys */
  const eligibleNext = useMemo(
    () => getNextEligibleFilters(kpiList, filterSteps),
    [kpiList, filterSteps]
  );

  const nextKeys = useMemo(() => {
    const activeKeys = new Set(activeSteps.map((s) => s.key));
    return eligibleNext.filter((k) => !activeKeys.has(k.key)).map((k) => k.key);
  }, [eligibleNext, activeSteps]);

  /* Values for the currently open dropdown */
  const openValues = useMemo(() => {
    if (!openKey) return [];

    const stepIdx = activeSteps.findIndex((s) => s.key === openKey);
    // For an already-active key: use the prefix UP TO (not including) this step
    const prefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;

    const vals = new Set();
    for (const item of kpiList) {
      for (const combo of parseCascadeTags(item.rawTags)) {
        let pos = 0;
        let ok = true;
        for (const step of prefix) {
          if (
            pos >= combo.length ||
            combo[pos].key !== step.key ||
            !step.values.has(combo[pos].value)
          ) {
            ok = false;
            break;
          }
          pos++;
        }
        if (ok && combo[pos]?.key === openKey && combo[pos]?.value) {
          vals.add(combo[pos].value);
        }
      }
    }
    return Array.from(vals).sort();
  }, [openKey, filterSteps, activeSteps, kpiList]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find((s) => s.key === openKey);
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  /* selected / total count per value in the open dropdown */
  const getValueCounts = useCallback(
    (key, value) => {
      const stepIdx = activeSteps.findIndex((s) => s.key === key);
      const prefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;

      const matching = kpiList.filter((item) => {
        for (const combo of parseCascadeTags(item.rawTags)) {
          let pos = 0;
          let ok = true;
          for (const step of prefix) {
            if (
              pos >= combo.length ||
              combo[pos].key !== step.key ||
              !step.values.has(combo[pos].value)
            ) {
              ok = false;
              break;
            }
            pos++;
          }
          if (ok && combo[pos]?.key === key && combo[pos]?.value === value) return true;
        }
        return false;
      });

      const afterFilter = kpiList.filter((item) =>
        kpiMatchesCascadeSteps(item, filterSteps)
      );

      return {
        total: matching.length,
        selected: matching.filter((item) => afterFilter.some((f) => f.id === item.id)).length,
      };
    },
    [kpiList, activeSteps, filterSteps]
  );

  /* How many KPIs match all current active steps */
  const matchCount = useMemo(
    () => kpiList.filter((item) => kpiMatchesCascadeSteps(item, filterSteps)).length,
    [kpiList, filterSteps]
  );

  /* ── Mutations ── */
  const toggleValue = (key, value) => {
    const next = filterSteps.map((s) => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex((s) => s.key === key);

    if (idx === -1) {
      next.push({ key, values: new Set([value]) });
    } else {
      const vals = next[idx].values;
      if (vals.has(value)) vals.delete(value);
      else vals.add(value);

      if (!vals.size) {
        // Cascade reset: remove this step and all downstream steps
        next.splice(idx, next.length - idx);
        setOpenKey(null);
      }
    }
    onFilterStepsChange?.(next);
  };

  const removeStep = (key) => {
    const idx = filterSteps.findIndex((s) => s.key === key);
    if (idx >= 0) {
      onFilterStepsChange?.(filterSteps.slice(0, idx));
      setOpenKey(null);
    }
  };

  const clearAll = (e) => {
    e?.stopPropagation();
    onFilterStepsChange?.([]);
    setOpenKey(null);
  };

  const hasAny = activeSteps.length > 0 || nextKeys.length > 0;

  /* ── Shared micro-styles ── */
  const S = {
    chip: (isOpen, isActive) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 8px 4px 10px",
      borderRadius: 7,
      cursor: "pointer",
      border: `1.5px solid ${isOpen ? "#3b82f6" : isActive ? "#93c5fd" : "#e2e8f0"}`,
      background: isOpen ? "#eff6ff" : isActive ? "#dbeafe" : "#fff",
      fontSize: 11,
      fontWeight: 600,
      color: isOpen || isActive ? "#1d4ed8" : "#64748b",
      transition: "all 0.15s",
      userSelect: "none",
    }),
    countBadge: (active) => ({
      fontSize: 9,
      fontWeight: 800,
      padding: "1px 5px",
      borderRadius: 8,
      marginLeft: 2,
      background: active ? "#bfdbfe" : "#f1f5f9",
      color: active ? "#1e40af" : "#475569",
    }),
    arrow: { color: "#bae6fd", fontSize: 15, lineHeight: 1, userSelect: "none" },
    dropdown: {
      position: "absolute",
      top: "calc(100% + 6px)",
      left: 0,
      zIndex: 600,
      background: "#fff",
      border: "1.5px solid #e5e7eb",
      borderRadius: 10,
      boxShadow: "0 4px 6px -1px rgba(0,0,0,.07), 0 16px 48px -4px rgba(0,0,0,.12)",
      minWidth: 240,
      overflow: "hidden",
    },
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* ── Cascade row ── */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", minHeight: 34 }}>

        {/* Label */}
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#94a3b8",
          textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0,
        }}>
          Tag Filter:
        </span>

        {/* Active step chips */}
        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;
          return (
            <span key={step.key} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              {i > 0 && <span style={S.arrow}>›</span>}
              <button
                type="button"
                onClick={() => setOpenKey(isOpen ? null : step.key)}
                style={S.chip(isOpen, true)}
              >
                <span style={{ color: "#3b82f6", fontWeight: 700 }}>{step.key}:</span>
                <span style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                <span style={S.countBadge(true)}>{matchCount}</span>
                <span style={{ fontSize: 9, color: "#93c5fd" }}>▾</span>
                {/* Remove this step + cascade reset */}
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); removeStep(step.key); }}
                  style={{
                    fontSize: 14, lineHeight: 1, color: "#93c5fd",
                    fontWeight: 900, marginLeft: 1, padding: "0 1px",
                    transition: "color 0.15s", cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#93c5fd")}
                >
                  ×
                </span>
              </button>
            </span>
          );
        })}

        {/* Arrow into next eligible key */}
        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={S.arrow}>›</span>
        )}

        {/* Next eligible key pills */}
        {nextKeys.map((key) => {
          const isOpen = openKey === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setOpenKey(isOpen ? null : key)}
              style={S.chip(isOpen, false)}
            >
              {key} <span style={{ fontSize: 9 }}>▾</span>
            </button>
          );
        })}

        {/* Empty state */}
        {!hasAny && (
          <span style={{ fontSize: 11.5, color: "#9ca3af", fontStyle: "italic" }}>
            No tags available for current KPIs
          </span>
        )}

        {/* Clear all */}
        {activeSteps.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              fontSize: 10, color: "#ef4444", border: "none",
              background: "none", cursor: "pointer", fontWeight: 700, marginLeft: 4,
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── Value picker dropdown ── */}
      {openKey && openValues.length > 0 && (
        <div style={S.dropdown}>
          {/* Header */}
          <div style={{
            padding: "7px 12px",
            background: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <span style={{
              fontSize: 10, fontWeight: 800, color: "#475569",
              textTransform: "uppercase", letterSpacing: "0.6px",
            }}>
              {openKey}
            </span>
            <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>selected / total</span>
          </div>

          {/* Values list */}
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {openValues.map((val) => {
              const { total, selected } = getValueCounts(openKey, val);
              const isChecked = openSelectedVals.has(val);
              return (
                <div
                  key={val}
                  onClick={() => toggleValue(openKey, val)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: isChecked ? "#f0f9ff" : "transparent",
                    borderBottom: "1px solid #f8fafc",
                    transition: "background 0.1s",
                  }}
                >
                  {/* Custom checkbox */}
                  <div style={{
                    width: 15, height: 15, borderRadius: 3, flexShrink: 0,
                    border: `1.5px solid ${isChecked ? "#6366f1" : "#d1d5db"}`,
                    background: isChecked ? "#6366f1" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.12s",
                  }}>
                    {isChecked && (
                      <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                        <path
                          d="M1.5 4.5l2.5 2.5 4-4"
                          stroke="#fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>

                  <span style={{
                    flex: 1, fontSize: 12,
                    color: "#374151",
                    fontWeight: isChecked ? 600 : 400,
                  }}>
                    {val}
                  </span>

                  {/* selected / total badge */}
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: isChecked ? "#dbeafe" : "#f1f5f9",
                    color: isChecked ? "#1d4ed8" : "#64748b",
                    borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap",
                    border: `1px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`,
                  }}>
                    {selected}/{total}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {activeSteps.length > 0 && (
            <div style={{
              padding: "7px 14px",
              borderTop: "1px solid #f3f4f6",
              background: "#f9fafb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span style={{ fontSize: 10.5, color: "#6b7280", fontWeight: 500 }}>
                {activeSteps.length} filter{activeSteps.length !== 1 ? "s" : ""} active
                {" · "}
                {matchCount} KPI{matchCount !== 1 ? "s" : ""} match
              </span>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  fontSize: 10.5, color: "#6366f1", background: "none",
                  border: "none", cursor: "pointer", fontWeight: 700,
                  padding: 0, fontFamily: "inherit",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KpiCascadeFilter;